import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";

function MenuItem({ item, tenant, depth, isActive, canEdit, sidebarCollapsed, tLabel, onNavigate }) {
  const Icon = item.icon;
  const active = isActive(item.path);
  const paddingLeft = sidebarCollapsed ? 'px-2' : `pl-${4 + depth * 4}`;
  const isReadOnly = !canEdit(item.path);

  return (
    <Link
      key={item.path}
      to={`/${tenant}/${item.path}`}
      onClick={onNavigate}
      className={`flex items-center gap-3 ${paddingLeft} pr-3 py-2 rounded-md text-sm transition-colors ${
        active ? "bg-primary text-primary-foreground" : "hover:bg-muted text-muted-foreground hover:text-foreground"
      } ${isReadOnly ? 'opacity-75' : ''}`}
      title={sidebarCollapsed ? tLabel(item) : (isReadOnly ? `${tLabel(item)} (sola lettura)` : undefined)}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      {!sidebarCollapsed && (
        <span className="truncate flex items-center gap-1">
          {tLabel(item)}
          {isReadOnly && <span className="text-[10px] text-amber-500 font-medium">(R)</span>}
        </span>
      )}
    </Link>
  );
}

function SubgroupSection({ subgroup, tenant, depth, expandedGroups, toggleGroup, isActive, canView, canEdit, sidebarCollapsed, tLabel, onNavigate }) {
  const Icon = subgroup.icon;
  const isExpanded = expandedGroups[subgroup.id];
  const visibleSubItems = subgroup.items?.filter(item => canView(item.path));
  if (!visibleSubItems?.length) return null;
  const hasActiveChild = visibleSubItems.some(item => isActive(item.path));

  return (
    <Collapsible key={subgroup.id} open={isExpanded} onOpenChange={() => toggleGroup(subgroup.id)}>
      <CollapsibleTrigger asChild>
        <button className={`flex items-center justify-between w-full pl-${4 + depth * 2} pr-3 py-2 rounded-md text-sm transition-colors ${hasActiveChild ? "bg-primary/10 text-primary" : "hover:bg-muted text-muted-foreground hover:text-foreground"}`}>
          <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 flex-shrink-0" />
            <span>{tLabel(subgroup)}</span>
          </div>
          {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-2 space-y-1 mt-1">
        {visibleSubItems.map(item => (
          <MenuItem key={item.path} item={item} tenant={tenant} depth={depth + 1} isActive={isActive} canEdit={canEdit} sidebarCollapsed={sidebarCollapsed} tLabel={tLabel} onNavigate={onNavigate} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function GroupSection({ group, tenant, expandedGroups, toggleGroup, isActive, canView, canEdit, sidebarCollapsed, tLabel, onNavigate }) {
  const Icon = group.icon;
  const isExpanded = expandedGroups[group.id];

  const visibleItems = group.items?.filter(item =>
    item.type === 'subgroup' ? item.items?.some(sub => canView(sub.path)) : canView(item.path)
  );
  if (!visibleItems?.length) return null;

  const hasActiveChild = visibleItems.some(item =>
    item.type === 'subgroup' ? item.items?.some(sub => isActive(sub.path)) : isActive(item.path)
  );

  if (sidebarCollapsed) {
    return (
      <DropdownMenu key={group.id}>
        <DropdownMenuTrigger asChild>
          <button className={`flex items-center justify-center w-full p-2 rounded-md text-sm transition-colors ${hasActiveChild ? "bg-primary/10 text-primary" : "hover:bg-muted text-muted-foreground"}`} title={tLabel(group)}>
            <Icon className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" className="w-56">
          <div className="px-2 py-1.5 text-sm font-semibold">{tLabel(group)}</div>
          <DropdownMenuSeparator />
          {visibleItems.map((item) => {
            if (item.type === 'subgroup') {
              const subs = item.items?.filter(sub => canView(sub.path));
              if (!subs?.length) return null;
              return (
                <div key={item.id}>
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{tLabel(item)}</div>
                  {subs.map(subItem => (
                    <DropdownMenuItem key={subItem.path} asChild>
                      <Link to={`/${tenant}/${subItem.path}`} className="flex items-center gap-2">
                        <subItem.icon className="h-4 w-4" />{tLabel(subItem)}
                        {!canEdit(subItem.path) && <span className="text-[10px] text-amber-500">(R)</span>}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </div>
              );
            }
            return (
              <DropdownMenuItem key={item.path} asChild>
                <Link to={`/${tenant}/${item.path}`} className="flex items-center gap-2">
                  <item.icon className="h-4 w-4" />{tLabel(item)}
                  {!canEdit(item.path) && <span className="text-[10px] text-amber-500">(R)</span>}
                </Link>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Collapsible key={group.id} open={isExpanded} onOpenChange={() => toggleGroup(group.id)}>
      <CollapsibleTrigger asChild>
        <button className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-sm transition-colors ${hasActiveChild ? "bg-primary/10 text-primary" : "hover:bg-muted text-muted-foreground hover:text-foreground"}`}>
          <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 flex-shrink-0" />
            <span className="font-medium">{tLabel(group)}</span>
          </div>
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-2 space-y-1 mt-1">
        {visibleItems.map((item) => {
          if (item.type === 'subgroup') {
            return <SubgroupSection key={item.id} subgroup={item} tenant={tenant} depth={1} expandedGroups={expandedGroups} toggleGroup={toggleGroup} isActive={isActive} canView={canView} canEdit={canEdit} sidebarCollapsed={sidebarCollapsed} tLabel={tLabel} onNavigate={onNavigate} />;
          }
          return <MenuItem key={item.path} item={item} tenant={tenant} depth={1} isActive={isActive} canEdit={canEdit} sidebarCollapsed={sidebarCollapsed} tLabel={tLabel} onNavigate={onNavigate} />;
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function SidebarNavigation({ menuStructure, tenant, expandedGroups, toggleGroup, isActive, canView, canEdit, sidebarCollapsed, tLabel, onNavigate }) {
  return menuStructure.map(item => {
    if (item.type === 'group') {
      return <GroupSection key={item.id} group={item} tenant={tenant} expandedGroups={expandedGroups} toggleGroup={toggleGroup} isActive={isActive} canView={canView} canEdit={canEdit} sidebarCollapsed={sidebarCollapsed} tLabel={tLabel} onNavigate={onNavigate} />;
    }
    if (!canView(item.path)) return null;
    return <MenuItem key={item.path} item={item} tenant={tenant} depth={0} isActive={isActive} canEdit={canEdit} sidebarCollapsed={sidebarCollapsed} tLabel={tLabel} onNavigate={onNavigate} />;
  }).filter(Boolean);
}
